import { useCallback, useEffect, useRef, useState } from "react";

import { getGameLeaderboard, postGameRun } from "../api/api";
import { Button } from "../components/Button";
import { GECKO, loadGameSprites, spriteReady } from "../game/gameSprites";

const SMOKE_PTS = 12;
const SMOKE_PTS_PREMIUM = 18;
/** Множитель «игрового» dt при синем дыме (замедление). */
const SLOW_TIME_MULT = 0.46;
const SLOW_DURATION_MS = 4800;
const MAGNET_DURATION_MS = 6000;
const MAGNET_PULL_PX_S = 280;
const GRAVITY = 2600;
const JUMP_V = -780;
const PLAYER_W = 36;
const PLAYER_H = 30;
const PLAYER_X = 68;
const GROUND_PAD = 40;
const SMOKE_R = 15;
const COAL_W = 38;
const COAL_H = 30;
const HIT_INSET = 5;
/** Визуальный размер спрайта дыма (центр в e.x, e.y). */
const SMOKE_SPRITE = SMOKE_R * 2.25;

/** База и рост скорости как раньше к 1-й минуте; потолок достигается к 15-й минуте и не растёт. */
const SCROLL_BASE = 200;
const SCROLL_REF_SEC = 60;
const SCROLL_CAP =
  SCROLL_BASE + 28 * SCROLL_REF_SEC + 0.35 * SCROLL_REF_SEC * SCROLL_REF_SEC;
const SCROLL_RAMP_SEC = 15 * 60;
/** Интервал спавна (сек): от мягкого старта до «как после ~1 мин» к 15-й минуте. */
const SPAWN_INTERVAL_MAX = 1.45;
const SPAWN_INTERVAL_MIN = 0.52;
const COAL_CHANCE_MIN = 0.18;
const COAL_CHANCE_MAX = 0.42;
/** Потолок для `duration_ms` в БД (PostgreSQL INTEGER signed). */
const DURATION_MS_DB_MAX = 2_147_483_647;

function smoothstep01(u) {
  const t = clamp(u, 0, 1);
  return t * t * (3 - 2 * t);
}

function scrollPxAt(elapsedSec) {
  return SCROLL_BASE + (SCROLL_CAP - SCROLL_BASE) * smoothstep01(elapsedSec / SCROLL_RAMP_SEC);
}

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function circleRectOverlap(cx, cy, r, rx, ry, rw, rh) {
  const nx = clamp(cx, rx, rx + rw);
  const ny = clamp(cy, ry, ry + rh);
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy <= r * r;
}

function rectOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function isSmokeType(t) {
  return t === "smoke" || t === "smoke_slow" || t === "smoke_magnet";
}

function smokePointsFor(t) {
  if (t === "smoke_slow" || t === "smoke_magnet") return SMOKE_PTS_PREMIUM;
  return SMOKE_PTS;
}

function applySmokePickup(sim, nowMs, t) {
  sim.score += smokePointsFor(t);
  if (t === "smoke_slow") {
    sim.slowUntil = Math.max(sim.slowUntil, nowMs + SLOW_DURATION_MS);
  }
  if (t === "smoke_magnet") {
    sim.magnetUntil = Math.max(sim.magnetUntil, nowMs + MAGNET_DURATION_MS);
  }
}

function fillRoundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, rr);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();
}

function strokeRoundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, rr);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.stroke();
}

function drawSpriteBox(ctx, img, x, y, w, h) {
  if (!spriteReady(img)) return false;
  ctx.drawImage(img, x, y, w, h);
  return true;
}

function drawSpriteCentered(ctx, img, cx, cy, w, h) {
  if (!spriteReady(img)) return false;
  ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
  return true;
}

const GECKO_IDLE_MS = 220;
const GECKO_RUN_MS = 85;
const GECKO_JUMP_MS = 100;
const GECKO_FALL_MS = 110;

function geckoAnimRow(sim, phase) {
  if (phase !== "playing") return GECKO.rowIdle;
  if (sim.grounded) return GECKO.rowRun;
  return sim.playerVy < 0 ? GECKO.rowJump : GECKO.rowFall;
}

function geckoFrameMs(sim, phase) {
  if (phase !== "playing") return GECKO_IDLE_MS;
  if (sim.grounded) return GECKO_RUN_MS;
  return sim.playerVy < 0 ? GECKO_JUMP_MS : GECKO_FALL_MS;
}

/** PNG-лист геккона (`gecko_full_spritesheet.png`); иначе SVG ящерица / прямоугольник. */
function drawPlayer(ctx, sim, phase, sp, nowMs) {
  const { playerY } = sim;
  const sheet = sp.gecko;
  if (spriteReady(sheet)) {
    const row = geckoAnimRow(sim, phase);
    const frameMs = geckoFrameMs(sim, phase);
    const col = Math.floor((nowMs - sim.t0) / frameMs) % GECKO.cols;
    const sx = col * GECKO.frameW;
    const sy = row * GECKO.frameH;
    ctx.drawImage(
      sheet,
      sx,
      sy,
      GECKO.frameW,
      GECKO.frameH,
      PLAYER_X,
      playerY,
      PLAYER_W,
      PLAYER_H,
    );
    return;
  }
  if (!drawSpriteBox(ctx, sp.lizard, PLAYER_X, playerY, PLAYER_W, PLAYER_H)) {
    ctx.fillStyle = "#2e7d32";
    fillRoundRect(ctx, PLAYER_X, playerY, PLAYER_W, PLAYER_H, 8);
    ctx.strokeStyle = "rgba(129, 199, 132, 0.9)";
    ctx.lineWidth = 2;
    strokeRoundRect(ctx, PLAYER_X, playerY, PLAYER_W, PLAYER_H, 8);
  }
}

/** Изометрический кубик угля (fallback без SVG). Координаты как в `public/game/coal.svg` (40×36). */
function drawHookahCoalCube(ctx, x, y, w, h) {
  const sx = w / 40;
  const sy = h / 36;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(sx, sy);
  ctx.lineJoin = "round";
  ctx.lineWidth = 0.15;
  ctx.beginPath();
  ctx.moveTo(22, 10);
  ctx.lineTo(36, 18);
  ctx.lineTo(36, 28);
  ctx.lineTo(22, 22);
  ctx.closePath();
  ctx.fillStyle = "#1a1512";
  ctx.fill();
  ctx.strokeStyle = "#3e2723";
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(22, 10);
  ctx.lineTo(8, 18);
  ctx.lineTo(8, 28);
  ctx.lineTo(22, 22);
  ctx.closePath();
  ctx.fillStyle = "#2d2620";
  ctx.fill();
  ctx.strokeStyle = "#4e342e";
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(22, 4);
  ctx.lineTo(36, 12);
  ctx.lineTo(22, 20);
  ctx.lineTo(8, 12);
  ctx.closePath();
  ctx.fillStyle = "#3d3630";
  ctx.fill();
  ctx.strokeStyle = "#5d4e47";
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(22, 6);
  ctx.lineTo(32, 12);
  ctx.lineTo(22, 17);
  ctx.lineTo(12, 12);
  ctx.closePath();
  ctx.strokeStyle = "rgba(255, 112, 67, 0.42)";
  ctx.stroke();
  ctx.restore();
}

function createSim(canvasW, canvasH) {
  const groundY = canvasH - GROUND_PAD;
  const startY = groundY - PLAYER_H;
  return {
    t0: performance.now(),
    lastNow: performance.now(),
    canvasW,
    canvasH,
    groundY,
    playerY: startY,
    playerVy: 0,
    grounded: true,
    entities: [],
    score: 0,
    spawnAcc: 0,
    nextId: 1,
    scrollPx: scrollPxAt(0),
    endReason: null,
    _lastScoreUi: 0,
    slowUntil: 0,
    magnetUntil: 0,
  };
}

function spawnEntity(sim, nowMs) {
  const { canvasW, groundY } = sim;
  const elapsedSec = (nowMs - sim.t0) / 1000;
  const ramp = smoothstep01(elapsedSec / SCROLL_RAMP_SEC);
  const coalChance = COAL_CHANCE_MIN + (COAL_CHANCE_MAX - COAL_CHANCE_MIN) * ramp;
  const isCoal = Math.random() < coalChance;
  const x = canvasW + 24;
  const top = 48;
  const bottom = groundY - (isCoal ? COAL_H : SMOKE_R * 2) - 8;
  const y = top + Math.random() * Math.max(12, bottom - top);
  if (isCoal) {
    sim.entities.push({
      id: sim.nextId++,
      type: "coal",
      x,
      y,
      w: COAL_W,
      h: COAL_H,
    });
  } else {
    const rSub = Math.random();
    const pBlue = 0.055 + 0.04 * ramp;
    const pGold = 0.045 + 0.035 * ramp;
    let st = "smoke";
    if (rSub < pBlue) st = "smoke_slow";
    else if (rSub < pBlue + pGold) st = "smoke_magnet";
    sim.entities.push({
      id: sim.nextId++,
      type: st,
      x: x + SMOKE_R,
      y: y + SMOKE_R,
      r: SMOKE_R,
    });
  }
}

function drawFrame(ctx, sim, phase, sprites) {
  const { canvasW, canvasH, groundY, entities, score, endReason } = sim;
  const sp = sprites || {};
  ctx.clearRect(0, 0, canvasW, canvasH);
  const g = ctx.createLinearGradient(0, 0, 0, canvasH);
  g.addColorStop(0, "#1a2a28");
  g.addColorStop(1, "#0f1614");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, canvasW, canvasH);

  ctx.fillStyle = "rgba(32, 178, 170, 0.12)";
  ctx.fillRect(0, groundY, canvasW, canvasH - groundY);
  ctx.strokeStyle = "rgba(159, 217, 214, 0.35)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, groundY + 0.5);
  ctx.lineTo(canvasW, groundY + 0.5);
  ctx.stroke();

  const hudNow = performance.now();
  for (const e of entities) {
    if (e.type === "coal") {
      if (!drawSpriteBox(ctx, sp.coal, e.x, e.y, e.w, e.h)) {
        drawHookahCoalCube(ctx, e.x, e.y, e.w, e.h);
      }
    } else if (e.type === "smoke_magnet") {
      if (!drawSpriteCentered(ctx, sp.smoke_gold, e.x, e.y, SMOKE_SPRITE, SMOKE_SPRITE)) {
        const g0 = ctx.createRadialGradient(e.x, e.y, 2, e.x, e.y, e.r * 1.2);
        g0.addColorStop(0, "rgba(255, 236, 160, 0.95)");
        g0.addColorStop(0.55, "rgba(232, 197, 71, 0.75)");
        g0.addColorStop(1, "rgba(180, 140, 40, 0.25)");
        ctx.fillStyle = g0;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(255, 220, 120, 0.65)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r * 0.92, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (e.type === "smoke_slow") {
      if (!drawSpriteCentered(ctx, sp.smoke_blue, e.x, e.y, SMOKE_SPRITE, SMOKE_SPRITE)) {
        ctx.fillStyle = "rgba(100, 170, 255, 0.55)";
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r * 1.05, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(130, 200, 255, 0.85)";
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r * 0.72, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255, 255, 255, 0.65)";
        ctx.beginPath();
        ctx.arc(e.x - 2, e.y - 2, e.r * 0.28, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (!drawSpriteCentered(ctx, sp.smoke, e.x, e.y, SMOKE_SPRITE, SMOKE_SPRITE)) {
      ctx.fillStyle = "rgba(159, 217, 214, 0.45)";
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
      ctx.beginPath();
      ctx.arc(e.x - 3, e.y - 3, e.r * 0.35, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  drawPlayer(ctx, sim, phase, sp, hudNow);

  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = "600 14px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`Очки: ${score}`, 10, 22);
  if (phase === "playing") {
    ctx.textAlign = "right";
    ctx.fillText(formatElapsed(performance.now() - sim.t0), canvasW - 10, 22);
    let ix = canvasW - 12;
    if (sim.magnetUntil > hudNow) {
      ctx.fillStyle = "#e8c547";
      ctx.beginPath();
      ctx.arc(ix, 40, 5, 0, Math.PI * 2);
      ctx.fill();
      ix -= 14;
    }
    if (sim.slowUntil > hudNow) {
      ctx.fillStyle = "#6eb3ff";
      ctx.beginPath();
      ctx.arc(ix, 40, 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (phase === "ended" && endReason === "coal") {
    ctx.fillStyle = "rgba(255, 180, 180, 0.95)";
    ctx.font = "700 18px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Угли!", canvasW / 2, canvasH / 2 - 8);
  }
}

function resizeSimCanvas(sim, w, h) {
  sim.canvasW = w;
  sim.canvasH = h;
  sim.groundY = h - GROUND_PAD;
  sim.playerY = Math.min(sim.playerY, sim.groundY - PLAYER_H);
}

export function Game({ onBack }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const simRef = useRef(null);
  const phaseRef = useRef("idle");
  const rafRef = useRef(null);
  const gameLoopRef = useRef(() => {});
  const spritesRef = useRef(null);

  const [phase, setPhase] = useState("idle");
  const [spriteEpoch, setSpriteEpoch] = useState(0);
  const [score, setScore] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [endReason, setEndReason] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [boards, setBoards] = useState({
    day: [],
    month: [],
    all_time: [],
  });

  const loadBoards = useCallback(async () => {
    try {
      const [day, month, all_time] = await Promise.all([
        getGameLeaderboard("day"),
        getGameLeaderboard("month"),
        getGameLeaderboard("all_time"),
      ]);
      setBoards({ day, month, all_time });
    } catch (err) {
      setError(err?.message || "Не удалось загрузить таблицы лидеров");
    }
  }, []);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  useEffect(() => {
    let cancelled = false;
    loadGameSprites()
      .then((map) => {
        if (!cancelled) {
          spritesRef.current = map;
          setSpriteEpoch((n) => n + 1);
        }
      })
      .catch(() => {
        if (!cancelled) {
          spritesRef.current = null;
          setSpriteEpoch((n) => n + 1);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const syncCanvasSize = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return { w: 0, h: 0 };
    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(280, Math.floor(rect.width));
    const cssH = Math.min(320, Math.max(200, Math.floor(rect.width * 0.52)));
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { w: cssW, h: cssH };
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const finishRound = useCallback(
    (reason, sim) => {
      phaseRef.current = "ended";
      stopLoop();
      const elapsed = Math.round(performance.now() - sim.t0);
      sim.endReason = reason;
      setEndReason(reason);
      setScore(sim.score);
      setElapsedMs(elapsed);
      setPhase("ended");
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) drawFrame(ctx, sim, "ended", spritesRef.current);
    },
    [stopLoop],
  );

  useEffect(() => {
    gameLoopRef.current = () => {
      const canvas = canvasRef.current;
      const sim = simRef.current;
      if (!canvas || !sim || phaseRef.current !== "playing") return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const now = performance.now();
      const dt = clamp((now - sim.lastNow) / 1000, 0, 0.05);
      sim.lastNow = now;
      const elapsed = now - sim.t0;
      const slowMult = now < sim.slowUntil ? SLOW_TIME_MULT : 1;
      const effDt = dt * slowMult;
      const magnetActive = now < sim.magnetUntil;

      const elapsedSec = elapsed / 1000;
      sim.scrollPx = scrollPxAt(elapsedSec);
      const ramp = smoothstep01(elapsedSec / SCROLL_RAMP_SEC);
      const spawnEvery =
        SPAWN_INTERVAL_MAX - (SPAWN_INTERVAL_MAX - SPAWN_INTERVAL_MIN) * ramp;
      sim.spawnAcc += effDt;
      while (sim.spawnAcc >= spawnEvery) {
        sim.spawnAcc -= spawnEvery;
        spawnEntity(sim, now);
      }

      sim.playerVy += GRAVITY * effDt;
      sim.playerY += sim.playerVy * effDt;
      if (sim.playerY + PLAYER_H >= sim.groundY) {
        sim.playerY = sim.groundY - PLAYER_H;
        sim.playerVy = 0;
        sim.grounded = true;
      } else {
        sim.grounded = false;
      }

      const px = PLAYER_X + HIT_INSET;
      const py = sim.playerY + HIT_INSET;
      const pw = PLAYER_W - HIT_INSET * 2;
      const ph = PLAYER_H - HIT_INSET * 2;
      const pcx = PLAYER_X + PLAYER_W / 2;
      const pcy = sim.playerY + PLAYER_H / 2;
      const move = sim.scrollPx * effDt;
      const next = [];
      let hitCoal = false;
      for (const e of sim.entities) {
        if (isSmokeType(e.type)) {
          e.x -= move;
          if (magnetActive) {
            const dx = pcx - e.x;
            const dy = pcy - e.y;
            const len = Math.hypot(dx, dy) || 1;
            const pull = MAGNET_PULL_PX_S * effDt;
            e.x += (dx / len) * pull;
            e.y += (dy / len) * pull * 0.42;
          }
          if (circleRectOverlap(e.x, e.y, e.r * 0.85, px, py, pw, ph)) {
            applySmokePickup(sim, now, e.type);
          } else if (e.x + e.r > -20) {
            next.push(e);
          }
        } else if (e.type === "coal") {
          e.x -= move;
          if (rectOverlap(px, py, pw, ph, e.x, e.y, e.w, e.h)) {
            hitCoal = true;
          } else if (e.x + e.w > -20) {
            next.push(e);
          }
        }
      }
      sim.entities = next;

      if (sim.score !== sim._lastScoreUi) {
        sim._lastScoreUi = sim.score;
        setScore(sim.score);
      }

      if (hitCoal) {
        finishRound("coal", sim);
        drawFrame(ctx, sim, "ended", spritesRef.current);
        return;
      }

      drawFrame(ctx, sim, "playing", spritesRef.current);
      rafRef.current = requestAnimationFrame(() => gameLoopRef.current());
    };
  }, [finishRound]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const ro = new ResizeObserver(() => {
      if (phaseRef.current === "playing") return;
      const { w, h } = syncCanvasSize();
      if (w <= 0) return;
      const sim = simRef.current;
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx || !sim) return;
      resizeSimCanvas(sim, w, h);
      drawFrame(ctx, sim, phaseRef.current, spritesRef.current);
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [syncCanvasSize, spriteEpoch]);

  useEffect(() => {
    if (phase === "playing") return undefined;
    const { w, h } = syncCanvasSize();
    if (w <= 0) return undefined;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return undefined;
    if (phase === "ended") {
      const sim = simRef.current;
      if (sim) {
        resizeSimCanvas(sim, w, h);
        drawFrame(ctx, sim, "ended", spritesRef.current);
      }
      return undefined;
    }
    const sim = createSim(w, h);
    simRef.current = sim;
    drawFrame(ctx, sim, "idle", spritesRef.current);
    return undefined;
  }, [phase, syncCanvasSize, spriteEpoch]);

  const startRound = () => {
    setError("");
    setEndReason(null);
    stopLoop();
    const { w, h } = syncCanvasSize();
    if (w <= 0) return;
    const sim = createSim(w, h);
    sim._lastScoreUi = 0;
    simRef.current = sim;
    phaseRef.current = "playing";
    setPhase("playing");
    setScore(0);
    setElapsedMs(0);
    rafRef.current = requestAnimationFrame(() => gameLoopRef.current());
  };

  const onJump = () => {
    if (phaseRef.current !== "playing" || !simRef.current) return;
    const sim = simRef.current;
    if (sim.grounded) {
      sim.playerVy = JUMP_V;
      sim.grounded = false;
    }
  };

  const submitRun = async () => {
    const sim = simRef.current;
    const finalScore = sim?.score ?? score;
    const duration = Math.round(
      clamp(Math.max(1, elapsedMs), 1, DURATION_MS_DB_MAX),
    );
    setSubmitting(true);
    setError("");
    try {
      await postGameRun({
        score: finalScore,
        duration_ms: duration,
        run_meta_json: JSON.stringify({
          mode: "runner_canvas_v1",
          end: endReason || "coal",
          scroll_cap_px_s: SCROLL_CAP,
          scroll_ramp_sec: SCROLL_RAMP_SEC,
        }),
      });
      await loadBoards();
      phaseRef.current = "idle";
      setPhase("idle");
      setScore(0);
      setElapsedMs(0);
      setEndReason(null);
    } catch (err) {
      setError(err?.message || "Не удалось сохранить результат");
    } finally {
      setSubmitting(false);
    }
  };

  const playAgain = () => {
    stopLoop();
    phaseRef.current = "idle";
    setPhase("idle");
    setEndReason(null);
    setScore(0);
    setElapsedMs(0);
  };

  useEffect(() => () => stopLoop(), [stopLoop]);

  return (
    <section className="client gamePage">
      <header className="sectionHeader">
        <h2>Ящерка и дым</h2>
        <p>
          Бета для админов: раннер на canvas — тап / клик = прыжок, ловите дым (очки), избегайте
          углей. Премиум-дым: синий — замедление времени, золотой — обычный дым притягивается к
          ящерке. Лимита по времени нет; скорость полосы к 15-й минуте как после 1 мин раньше.
          Лидерборды: день, месяц, всё время. API пока только для администраторов.
        </p>
      </header>

      <div className="eventsToolbar">
        <Button onClick={onBack}>Назад в админку</Button>
      </div>

      {error ? <p className="errorMessage">{error}</p> : null}

      <div className="gamePage__panel">
        <div className="gamePage__stats">
          <span>Очки: {score}</span>
          <span>{phase === "playing" ? "Время на поле" : null}</span>
        </div>

        <div
          ref={wrapRef}
          className="gamePage__canvasWrap"
          role="presentation"
          onPointerDown={(e) => {
            if (phaseRef.current === "playing") {
              e.preventDefault();
              wrapRef.current?.setPointerCapture?.(e.pointerId);
              onJump();
            }
          }}
        >
          <canvas ref={canvasRef} className="gamePage__canvas" aria-label="Игровое поле" />
        </div>

        <p className="muted gamePage__hint">
          {phase === "idle"
            ? "Нажмите «Старт», затем тапайте по полю, чтобы прыгать."
            : phase === "playing"
              ? "Тап = прыжок. Синий — замедление, золотой — магнит к дыму. Угли — проигрыш."
              : "Раунд оборвался на углях."}
        </p>

        <div className="gamePage__actions">
          {phase === "idle" ? <Button onClick={startRound}>Старт</Button> : null}
          {phase === "ended" ? (
            <>
              <Button variant="accent" onClick={submitRun} disabled={submitting}>
                {submitting ? "Отправка…" : "Отправить в таблицу"}
              </Button>
              <Button onClick={playAgain} disabled={submitting}>
                Снова без отправки
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="gamePage__boards">
        {["day", "month", "all_time"].map((key) => (
          <article key={key} className="homeSurface gamePage__board">
            <h3 className="gamePage__boardTitle">
              {key === "day" ? "День" : key === "month" ? "Месяц" : "Всё время"}
            </h3>
            {boards[key].length === 0 ? (
              <p className="muted">Пока нет записей</p>
            ) : (
              <ol className="gamePage__boardList">
                {boards[key].map((row) => (
                  <li key={`${key}-${row.user_id}-${row.rank}`}>
                    <span className="gamePage__boardRank">{row.rank}.</span>{" "}
                    <span className="gamePage__boardName">{row.display_name}</span> —{" "}
                    <span className="gamePage__boardScore">{row.score}</span>
                  </li>
                ))}
              </ol>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
